import { FC, memo } from 'react';

import { ModalProps, ModalTypes } from 'application/providers/ModalProvider';
import { RouterUtils, useLocation, useNavigate } from 'application/providers/RouterProvider';
import { LocationStatePointsType } from 'application/pages/types';
import { POINTS_MENU_PATH, STREAMS_CATEGORY_PATH } from 'application/consts';
import ConnectWithManagerButton from 'application/components/ConnectWithManagerButton';
import { useAccountCompanyCountry, useAccountCompanyDeviceRequests } from 'application/hooks';

import ModalScreen from '@zvuk-b2b/react-uikit/ui/ModalScreen';
import IconMapPin from '@zvuk-b2b/react-uikit/ui/icons/MapPin';
import Button, { ButtonType } from '@zvuk-b2b/react-uikit/ui/Button';

import { CompanyDeviceRequestPureType } from 'domain/api/graphql/generated';

import useDeviceBindSuccessModalViewModel from './useDeviceBindSuccessModalViewModel';
import { useOnboardingContext } from '../useOnboardingContext';
import { useOnboardingActionContext } from '../useOnboardingActionContext';
import { TEXTS } from './texts';
import { ModalDescription, SuccessActionButton } from './components';
import { SelectPlayerFlowType } from '../types';

import styles from './index.module.scss';

const DeviceBindSuccessModal: FC<ModalProps[ModalTypes.DEVICE_BIND_SUCCESS]> = ({ close, replaceModal }) => {
  const { flowType, currentPoint } = useOnboardingContext();
  const { clearCurrentPoint, setFlowType } = useOnboardingActionContext();

  const companyCountry = useAccountCompanyCountry();
  const deviceRequests = useAccountCompanyDeviceRequests();

  const navigate = useNavigate();
  const location = useLocation<LocationStatePointsType>();

  const isBindStreamFlow = flowType === SelectPlayerFlowType.BIND_STREAM;
  const deviceRequestsByPoint = deviceRequests.reduce((acc, request) => {
    if (request.pointId === currentPoint.id) {
      acc.push(request);
    }

    return acc;
  }, [] as CompanyDeviceRequestPureType[]);

  const { addressHasStream, showWebPlayerButton, onOpenWebPlayerClick, onChangeStreamClick, onClose } =
    useDeviceBindSuccessModalViewModel({
      currentPoint,
      flowType,
      pathname: location.pathname,
      redirectToPointsPage: () => {
        navigate(RouterUtils.generatePath(POINTS_MENU_PATH));
      },
      redirectToStreamsPage: () => {
        navigate(
          RouterUtils.generatePath(STREAMS_CATEGORY_PATH, {
            category: 'recommended',
          }),
          {
            state: { pointsFromLocation: [{ pointId: currentPoint?.id, pointAddress: currentPoint?.address }] },
          }
        );
      },
      close,
      replaceModal,
      clearCurrentPoint,
      setFlowType,
    });

  return (
    <ModalScreen
      title={isBindStreamFlow ? TEXTS.MODAL_BIND_SUCCESS_TITLE_ON_REQUESTED : TEXTS.MODAL_BIND_SUCCESS_TITLE}
      titlePlacement="left"
      containerClassName={styles.container}
      onClose={onClose}
      actionButtons={
        isBindStreamFlow
          ? [
              <ConnectWithManagerButton
                key="button_to_manager_device_bind_success_modal"
                country={companyCountry}
              />,
              <Button
                key="button_cancel_bind_success_modal"
                elementId="button_cancel_bind_success_modal"
                type={ButtonType.SECONDARY}
                onClick={onClose}>
                {TEXTS.MODAL_BIND_SUCCESS_BUTTON_TO_STREAMS_TEXT}
              </Button>,
            ]
          : [
              <SuccessActionButton
                key="button_open_whatever_bind_success_modal"
                showStreamsButton={!addressHasStream}
                showWebPlayerButton={showWebPlayerButton}
                onChangeStreamClick={onChangeStreamClick}
                onOpenWebPlayerClick={onOpenWebPlayerClick}
                onClose={onClose}
              />,
            ]
      }>
      <ModalDescription
        addressHasStream={addressHasStream}
        isAddressEnabled={currentPoint?.isEnabled === true}
        isBindStreamFlow={isBindStreamFlow}
        deviceRequestsByPoint={deviceRequestsByPoint}
      />

      <div
        key={currentPoint?.id}
        className={styles.address}>
        <IconMapPin className={styles.addressIcon} />
        <span>{currentPoint?.address}</span>
      </div>
    </ModalScreen>
  );
};

export default memo(DeviceBindSuccessModal);
